import { useTranslation } from 'react-i18next';
import { RelatedDocument } from '@asset-metrix/portal-gateway-client-js';
import clsx from 'clsx';

import { mapMimeTypeToFileIcon } from '../../utils/mapMimeTypeToFileIcon';
import { SVGIcon } from '../icons/SVGIcon';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import styles from './document-listing.module.scss';

type DocumentListingProps = {
  className?: string;
  documents?: RelatedDocument[];
  loading?: boolean;
  onDocumentClick?: (document: RelatedDocument) => void;
  selectedDocument?: RelatedDocument;
};

export function DocumentListing({
  className,
  documents,
  loading,
  onDocumentClick,
  selectedDocument,
}: DocumentListingProps) {
  const { t } = useTranslation();

  const renderDocuments = () => {
    if (!documents || documents.length === 0) {
      return (
        <div
          className={styles['no-documents']}
          data-testid="no-documents-available"
        >
          {t('app.tableSidebarWidget.noDocumentsAvailable')}
        </div>
      );
    }

    return documents.map((document) => (
      <div
        className={clsx(styles['row'], {
          [styles['row--selected']]: document.id === selectedDocument?.id,
        })}
        onClick={() => onDocumentClick?.(document)}
        key={document.id}
        data-testid={`document-row-${document.id}`}
      >
        {document.virusFree === false ? (
          <TextWithTooltip
            tooltipProps={{
              disabled: false,
              label: t('app.table.tooltip.maliciousContent'),
            }}
          >
            <SVGIcon name="file-alert" />
          </TextWithTooltip>
        ) : (
          <SVGIcon name={mapMimeTypeToFileIcon(document.mimeType)} />
        )}
        <TextWithTooltip
          textProps={{
            className: styles['file-name'],
            lineClamp: 1,
          }}
          tooltipProps={{ usePortal: false }}
        >
          {document.fileName}
        </TextWithTooltip>
      </div>
    ));
  };

  return (
    <div
      data-testid="document-listing"
      className={clsx(styles['root'], className)}
    >
      {loading ? (
        <LoadingIndicator
          size="2em"
          style={{
            margin: '2em auto',
          }}
          data-testid="loading-indicator"
        />
      ) : (
        renderDocuments()
      )}
    </div>
  );
}
